import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { catchError, firstValueFrom, throwError } from 'rxjs';
import {
  CashRegister,
  CashRegisterAnalysis,
  OpenBalanceRequest,
  OpenBalanceResult,
  UserProfileResponse,
} from '../../interfaces/cash-register';

@Injectable({
  providedIn: 'root',
})
export class CashRegisterService {
  private apiURL = environment.apiUrl + '/v' + environment.apiVersion;

  constructor(private httpClient: HttpClient) {}

  public async getUserProfile(username: string): Promise<UserProfileResponse | null> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/CashRegister/UserProfile?username=${encodeURIComponent(username)}`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return null;
      return response.data[0] as UserProfileResponse;
    } catch {
      return null;
    }
  }

  public async getByUnit(unit: number, username: string): Promise<CashRegister[]> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(
            `${this.apiURL}/CashRegister/GetByUnit?unit=${unit}&username=${encodeURIComponent(username)}`,
          )
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return [];
      return response.data[0] as CashRegister[];
    } catch {
      return [];
    }
  }

  public async getBySequence(sequence: number, username: string): Promise<CashRegister | null> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(
            `${this.apiURL}/CashRegister/GetBySequence?sequence=${sequence}&username=${encodeURIComponent(username)}`,
          )
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return null;
      return response.data[0] as CashRegister;
    } catch {
      return null;
    }
  }

  public async getForBleed(cdEstabelecimento: number, excludeSequence: number, username: string): Promise<CashRegister[]> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(
            `${this.apiURL}/CashRegister/GetForBleed?cdEstabelecimento=${cdEstabelecimento}&excludeSequence=${excludeSequence}&username=${encodeURIComponent(username)}`,
          )
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return [];
      return response.data[0] as CashRegister[];
    } catch {
      return [];
    }
  }

  public async getAnalysisSummary(username: string, dataReferencia?: string, cdEmpresa?: string): Promise<CashRegisterAnalysis[]> {
    try {
      const dataParam = dataReferencia ? `&dataReferencia=${dataReferencia}` : '';
      const empresaParam = cdEmpresa ? `&cdEmpresa=${encodeURIComponent(cdEmpresa)}` : '';
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/CashRegister/AnalysisSummary?username=${encodeURIComponent(username)}${dataParam}${empresaParam}`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return [];
      return response.data[0] as CashRegisterAnalysis[];
    } catch {
      return [];
    }
  }

  public async openBalance(request: OpenBalanceRequest): Promise<OpenBalanceResult> {
    const response = await firstValueFrom(
      this.httpClient
        .post<any>(`${this.apiURL}/CashRegister/OpenBalance`, request)
        .pipe(catchError(this.handleError)),
    );
    if (!response?.data?.length) {
      throw new Error('Saldo não foi retornado pela API.');
    }
    return response.data[0] as OpenBalanceResult;
  }

  public async closeBalance(nrSeqSaldo: number, username: string): Promise<OpenBalanceResult> {
    const response = await firstValueFrom(
      this.httpClient
        .post<any>(`${this.apiURL}/CashRegister/CloseBalance`, { nrSeqSaldo, username })
        .pipe(catchError(this.handleError)),
    );
    if (!response?.data?.length) {
      throw new Error('Resposta vazia da API.');
    }
    return response.data[0] as OpenBalanceResult;
  }

  private handleError(error: HttpErrorResponse) {
    return throwError(() => error);
  }
}
